import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { queryClient } from '../lib/queryClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// SSO Stage 2: the backend now also issues a cw_session/cw_refresh/csrf_token
// cookie trio alongside the bearer token this file has always used. csrf_token
// is deliberately not httpOnly — it exists purely so JS can read it back and
// prove a mutating request came from this app, not a forged cross-site one.
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    const method = (config.method || 'get').toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      // A 401 might just mean the bearer token expired while a still-valid
      // cw_refresh cookie exists — try one silent refresh before giving up.
      // Uses raw axios, not `api`, so a failed refresh can't recurse back
      // into this same interceptor.
      if (error.response?.status === 401 && original && !original._retried) {
        original._retried = true;
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          return api(original);
        } catch (refreshError) {
          // fall through to the hard logout below
        }
      }
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        queryClient.clear();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const switchRole = async (newRole) => {
    await api.post('/auth/switch-role', { role: newRole });
    const response = await api.get('/auth/me');
    setUser(response.data);
    // Same reasoning as logout(): role-scoped dashboards share unscoped
    // query keys, so a stale cache would otherwise leak the old role's data.
    queryClient.clear();
    return response.data;
  };

  const logout = () => {
    // Local state is cleared immediately, so the UI never waits on the
    // network. The returned promise is for callers that navigate away right
    // after (e.g. LogoutPage) and need the revoke request to actually leave
    // the browser before the page unloads.
    const revoked = axios
      .post(`${API}/auth/logout`, {}, { withCredentials: true })
      .catch(() => {});
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    // Query keys (bookings.list, analytics.dashboard, etc.) aren't scoped by
    // user/role, so without this the next account to log in on this tab can
    // briefly see the previous account's cached server-state before it
    // refetches.
    queryClient.clear();
    return revoked;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    switchRole,
    logout,
    api,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'hub_manager',
    isCenterManager: user?.role === 'center_manager' || user?.role === 'cluster_manager',
    isClient: user?.role === 'client',
    isGuest: user?.role === 'guest'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
